/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Thursday, December 9th 2021, 12:34:24 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { useTranslation } from "react-i18next";
import "core/locales/i18n";

export const useTranslatedText = () => {
  const { t } = useTranslation();

  const LANG = {
    home: t("Home"),
    organizations: t("Organizations"),
    organization: t("Organization"),
    fetchingOrganizations: t("Fetching Organizations"),
    myOrganization: t("My Organization"),
    portals: t("Portals"),
    hostName: t("Host Name"),
    portalDisplayName: t("Portal Display Name"),
    googleAnalyticsKey: t("Google Analytics Key"),
    fieldIsRequired: t("This field is required"),
    generalDetails: t("General Details"),
    portalTemplateSettings: t("Portal Template Settings"),
    createNewPortal: t("Create New Portal"),
    editPortal: t("Edit Portal"),
    portalForm: t("Portal Form"),
    managePortals: t("Manage Portals"),
    manageContent: t("Manage Content"),
    missingOrganizationError: t("Kindly select an Organization before adding a new portal"),
    portalEmailSettings: t("Portal Email Settings"),
    portalAdvancedSettings: t("Advanced"),
    apiSettings: t("API Settings"),
    activationEmail: t("Activation Email"),
    emailNotificationsLayout: t("Email Notifications Layout"),
    htmlDesign: t("HTML Design"),
    internalCSS: t("Internal CSS"),
    title: t("Title"),
    label: t("Label"),
    layoutType: t("Layout Type"),
    chooseLayout: t("Choose Layout Type"),
    services: t("Services"),
    databanks: t("DataBanks"),
    datasets: t("Datasets"),
    forms: t("Forms"),
    manageForms: t("Manage Forms"),
    manageFormsDescription: t("Manage all forms created with the Form Builder"),
    createNewForm: t("Create New Form"),
    teams: t("Teams"),
    users: t("Users"),
    groups: t("Groups"),
    departments: t("Departments"),
    profile: t("Profile"),
    accountProfile: t("My Profile"),
    accountProfileDescription: t("Manage your account profile"),
    billing: t("Billing"),
    logout: t("Logout"),
    active: t("Active"),
    emailAddress: t("Email Address"),
    emailAddresses: t("Email Addresses"),
    inviteWorkers: t("Invite Workers"),
    manageTeams: t("Manage Teams"),
    stageSettingsDescription: t(
      "Stages allow steps in a workflow to be grouped, allowing for high level tracking on the progress of the workflow. Your workflow, however, will still work perfectly fine without stages."
    ),
    createNewDatabank: t("Create New Databank"),
    createNewDataset: t("Create New Dataset"),
    manageDatasets: t("Manage Datasets"),
    editDataset: t("Edit Dataset"),
    datasetDetails: t("Dataset Details"),
    datasetFields: t("Dataset Fields"),
    datasetName: t("Dataset Name"),
    datasetDescription: t("Dataset Description"),
    serviceBuilderFormInstructions: t("Provide information about the form."),
    serviceBuilderSubmissionFormDescription: t(
      "Submission forms collect data that kick starts your configured workflow. Forms can be submitted by clients or staff."
    ),
    serviceBuilderServiceFormDescription: t(
      "Service registration forms allow users to register specific details about themselves before being able to make submissions."
    ),
    serviceBuilderServiceNoData: t(
      "There are no service registration forms configured for this service. If you want users to register specific details about themselves before accessing this service, add a service registration form(s) by clicking on the add button."
    ),
    portalOnboardingFormInstructions: t("Provide information about the onboarding form."),
    portalOnboardingDescription: t(
      "Portal onboarding forms allow users to register specific details about themselves before being able to access your portal. Any portal with onboarding forms set will not be accessible until the user provides these details."
    ),
    portalOnboardingFormsEmptyStateMessage: t(
      "There are no portal onboarding forms configured for this portal. If you want users to register specific details about themselves before accessing this portal, add a portal onboarding form(s) by clicking on the add button."
    ),
    oauthLoginClientErrorTitle: t("Unable to Log you in"),
    oauthLoginClientErrorMessage: t(
      "An issue occured while attempting to log you in to your Onestop account due to a possible issue in your web browser session. Try again after clearing your web browser cache, session data and cookies. Also disable all browser extensions like ad blockers."
    ),
    more: t("More"),
    allWorkers: t("All Workers"),
    payments: t("Payments"),
    managePaymentMethods: t("Manage Payment Methods"),
    managePaymentMethodsDescription: t("Manage all payment methods associated with your organization"),
    mediaLibrary: t("Media Library"),
    manageMediaLibrary: t("Manage Media Library"),
    manageMediaLibraryDescription: t(
      "Manage a library of images and files that you upload and use in your organization's workspace."
    ),
    translations: t("Translations"),
    manageTranslations: t("Manage Translations"),
    manageTranslationsDescription: t("Manage all translations in your organization's workspace"),
    importPOFile: t("Import from PO File"),
    exportPOFile: t("Export to PO File"),
    comingSoon: t("Coming Soon"),
    comingSoonDescription: t(
      "This feature is coming soon. We are working hard to bring you this feature as soon as possible."
    ),
    translationsNotFound: t("Translations Not Found"),
    noContentToTranslate: t("No content available to translate"),
    addCustomListLayout: t("Add Custom List Layout"),
    noCustomListConfiguration: t("No custom list configuration"),
    noCustomListConfigurationDescription: t(
      "You don't have a custom list layout configuration for this process. Custom list layouts allow you to customize the view that displays a list of instances for this process. Display submission data, task review data, dataset values and more within the list view."
    ),
    noCustomListConfigurationCTA: t(
      "Do you need more complex customizations? Get in touch with us to use our developer API."
    ),
    addColumnsToDataTable: t("Add Columns to DataTable"),
    remove: t("Remove"),
    delete: t("Delete"),
    linkToDetailsPage: t("Link to Details Page"),
    visibleToStaffOnly: t("Visible to Workers Only"),
    order: t("Order"),
    addNewColumn: t("Add New Column"),
    manageDataTableColumns: t("Manage DataTable Columns"),
    sNo: t("S/No"),
    columnReference: t("Column Reference"),
    changeDataQueryTypeConfirmation: t(
      "Changing the data query type will remove all existing columns and you will loose your current configuration unless your copy it. Are you sure about this?"
    ),
    changeDataQueryType: t("Change Data Query Type"),
    autoSaveSuccessful: t("Auto Save Successful"),
    savedSuccessfully: t("Saved Successfully"),
    integrations: t("Integrations"),
    manageIntegrations: t("Manage Integrations"),
    manageIntegrationsDescription: t("Manage all integrations in your organization's workspace"),
    ProgressWidget: t("Progress Widget"),
    valueForDisplayDataMustBeNumber: t("The value for the data you want to display must be a number"),
  };

  return LANG;
};
